/**
 * ActivityFeed - Feed of running activities
 * Follows SocialFeed.tsx pattern
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { cn } from '@/utils/cn';
import { ActivityCard } from './ActivityCard';
import type { RunningFeedType } from '@/types/running';

interface ActivityFeedProps {
  type: RunningFeedType;
  userId?: string;
  clubId?: string;
  onUserClick?: (userId: string) => void;
  onActivityClick?: (activityId: string) => void;
}

export function ActivityFeed({
  type,
  userId,
  clubId,
  onUserClick,
  onActivityClick,
}: ActivityFeedProps) {
  const activityFeed = useRunningStore((state) => state.activityFeed);
  const feedLoading = useRunningStore((state) => state.feedLoading);
  const feedError = useRunningStore((state) => state.feedError);
  const hasMore = useRunningStore((state) => state.hasMore);
  const fetchActivityFeed = useRunningStore((state) => state.fetchActivityFeed);

  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState<'latest' | 'following' | 'nearby'>('latest');
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    // Wait a tick for zustand persist to rehydrate from localStorage
    const hydrationTimer = setTimeout(() => {
      setInitialLoadDone(true);

      // Only try to fetch from DB if we have no local data after hydration
      // Use a short timeout to avoid blocking on slow/failing DB
      const fetchTimer = setTimeout(() => {
        fetchActivityFeed({ type, club_id: clubId }, 0).catch(() => {
          // Silently fail - we'll show local data
        });
      }, 100);

      return () => clearTimeout(fetchTimer);
    }, 50); // Small delay for hydration

    return () => clearTimeout(hydrationTimer);
  }, [type, userId, clubId]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchActivityFeed({ type, club_id: clubId }, nextPage);
  };

  const tabs = [
    { id: 'latest' as const, label: 'Latest', icon: '🕐' },
    { id: 'following' as const, label: 'Following', icon: '👥' },
    { id: 'nearby' as const, label: 'Nearby', icon: '📍' },
  ];

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="px-4 flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all',
              activeTab === tab.id
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-slate-800/50 text-text-secondary hover:text-white'
            )}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error State */}
      {feedError && (
        <div className="mx-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-red-400 text-sm text-center">{feedError}</p>
        </div>
      )}

      {/* Empty State - only show after initial load attempt */}
      {initialLoadDone && activityFeed.length === 0 && (
        <div className="text-center py-12 px-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-800/80 border border-emerald-500/30 flex items-center justify-center"
          >
            <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
              {/* Motion lines */}
              <rect x="0" y="20" width="15" height="4" rx="2" fill="#06b6d4" />
              <rect x="3" y="28" width="13" height="4" rx="2" fill="#06b6d4" />
              <rect x="0" y="36" width="15" height="4" rx="2" fill="#06b6d4" />
              {/* Head - positioned at top of torso */}
              <circle cx="46" cy="16" r="6" fill="#06b6d4" />
              {/* Torso - leaning forward */}
              <path d="M44 22L38 38" stroke="#06b6d4" strokeWidth="6" strokeLinecap="round" />
              {/* Back arm - bent elbow pointing back */}
              <path d="M42 26L50 30L56 24" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              {/* Front arm - bent elbow pointing forward */}
              <path d="M40 28L32 24L24 28" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              {/* Back leg - extended straight back (to the left) */}
              <path d="M38 38L24 48L14 56" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              {/* Front leg - thigh forward, lower leg curling inward */}
              <path d="M38 38L52 48L46 60" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">No activities yet</h3>
          <p className="text-text-secondary mb-4">
            Start recording your runs to see them here!
          </p>
          <div className="text-sm text-text-secondary">
            Tap the + button to record your first run
          </div>
        </div>
      )}

      {/* Activity List */}
      <div className="px-4 space-y-4">
        {activityFeed.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <ActivityCard
              activity={activity}
              onUserClick={onUserClick}
              onActivityClick={onActivityClick}
            />
          </motion.div>
        ))}
      </div>

      {/* Loading State - only show if no activities and still loading */}
      {feedLoading && activityFeed.length === 0 && !initialLoadDone && (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-2 text-text-secondary">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full"
            />
            <span>Loading activities...</span>
          </div>
        </div>
      )}

      {/* Load More */}
      {!feedLoading && hasMore && activityFeed.length > 0 && (
        <div className="px-4 pb-4">
          <button
            onClick={handleLoadMore}
            className={cn(
              'w-full py-3 rounded-xl',
              'bg-slate-800/50 text-text-secondary',
              'hover:bg-slate-700/50 hover:text-white',
              'transition-colors text-sm font-medium'
            )}
          >
            Load more activities
          </button>
        </div>
      )}

      {/* End of Feed */}
      {!hasMore && activityFeed.length > 0 && (
        <div className="text-center py-8 text-text-secondary text-sm">
          You've reached the end! 🏁
        </div>
      )}
    </div>
  );
}

export default ActivityFeed;
